import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  Search,
  Delete,
  Visibility,
  Email,
  Person,
  Subject,
  AccessTime,
  FilterList
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { contactService } from '../services/api';
import ContactDetailDialog from '../components/ContactDetailDialog';

const Contacts = () => {
  const { t } = useTranslation();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, contact: null });
  const [detailDialog, setDetailDialog] = useState({ open: false, contact: null });
  const [stats, setStats] = useState({ total: 0, new: 0, read: 0, responded: 0 });

  useEffect(() => {
    fetchContacts();
    fetchStats();
  }, [statusFilter]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      console.log('📋 Fetching contacts with params:', { statusFilter, search });
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (search) {
        params.search = search;
      }
      const response = await contactService.getAll(params);
      console.log('📥 Contacts response:', response);

      // The API returns { success: true, data: { contacts: [...], pagination: {...} } }
      const contactsList = response.data?.contacts || response.contacts || [];
      console.log('✅ Contacts loaded:', contactsList.length);
      setContacts(contactsList);
    } catch (error) {
      console.error('❌ Error fetching contacts:', error);
      setSnackbar({
        open: true,
        message: t('error.fetchContacts') || 'Failed to fetch contacts',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      console.log('📊 Fetching contact stats...');
      const response = await contactService.getStats();
      console.log('📥 Stats response:', response);

      // The API returns { success: true, data: { total: ..., byStatus: {...} } }
      const statsData = response.data || response;
      const formattedStats = {
        total: statsData.total || 0,
        new: statsData.byStatus?.new || 0,
        read: statsData.byStatus?.read || 0,
        responded: statsData.byStatus?.responded || 0,
        archived: statsData.byStatus?.archived || 0,
      };
      console.log('✅ Stats loaded:', formattedStats);
      setStats(formattedStats);
    } catch (error) {
      console.error('❌ Error fetching stats:', error);
    }
  };

  const handleSearch = (event) => {
    setSearch(event.target.value);
  };

  const handleSearchSubmit = () => {
    fetchContacts();
  };

  const handleStatusChange = async (contactId, newStatus) => {
    try {
      await contactService.updateStatus(contactId, newStatus);
      setSnackbar({
        open: true,
        message: t('success.statusUpdated'),
        severity: 'success'
      });
      fetchContacts();
      fetchStats();
    } catch (error) {
      console.error('Error updating status:', error);
      setSnackbar({
        open: true,
        message: t('error.updateStatus'),
        severity: 'error'
      });
    }
  };

  const handleDelete = async () => {
    try {
      await contactService.delete(deleteDialog.contact._id);
      setDeleteDialog({ open: false, contact: null });
      setSnackbar({
        open: true,
        message: t('success.contactDeleted'),
        severity: 'success'
      });
      fetchContacts();
      fetchStats();
    } catch (error) {
      console.error('Error deleting contact:', error);
      setSnackbar({
        open: true,
        message: t('error.deleteContact'),
        severity: 'error'
      });
    }
  };

  const handleViewContact = (contact) => {
    setDetailDialog({ open: true, contact });
    // Mark as read if it's new
    if (contact.status === 'new') {
      handleStatusChange(contact._id, 'read');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return 'error';
      case 'read':
        return 'warning';
      case 'responded':
        return 'success';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'new':
        return t('contact.status.new');
      case 'read':
        return t('contact.status.read');
      case 'responded':
        return t('contact.status.responded');
      case 'archived':
        return t('contact.status.archived');
      default:
        return status;
    }
  };

  const filteredContacts = contacts.filter(contact =>
    contact.firstName.toLowerCase().includes(search.toLowerCase()) ||
    contact.lastName.toLowerCase().includes(search.toLowerCase()) ||
    contact.email.toLowerCase().includes(search.toLowerCase()) ||
    contact.subject.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('navigation.contacts')}
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('contact.stats.total')}
              </Typography>
              <Typography variant="h5">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('contact.stats.new')}
              </Typography>
              <Typography variant="h5" color="error">
                {stats.new}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('contact.stats.read')}
              </Typography>
              <Typography variant="h5" color="warning.main">
                {stats.read}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('contact.stats.responded')}
              </Typography>
              <Typography variant="h5" color="success.main">
                {stats.responded}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          placeholder={t('contact.searchPlaceholder')}
          value={search}
          onChange={handleSearch}
          onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>{t('contact.filterByStatus')}</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label={t('contact.filterByStatus')}
            startAdornment={<FilterList />}
          >
            <MenuItem value="all">{t('contact.status.all')}</MenuItem>
            <MenuItem value="new">{t('contact.status.new')}</MenuItem>
            <MenuItem value="read">{t('contact.status.read')}</MenuItem>
            <MenuItem value="responded">{t('contact.status.responded')}</MenuItem>
            <MenuItem value="archived">{t('contact.status.archived')}</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleSearchSubmit}>
          {t('common.search')}
        </Button>
      </Box>

      {/* Contacts Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('contact.name')}</TableCell>
              <TableCell>{t('contact.email')}</TableCell>
              <TableCell>{t('contact.subject')}</TableCell>
              <TableCell>{t('contact.status.label')}</TableCell>
              <TableCell>{t('contact.createdAt')}</TableCell>
              <TableCell align="right">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : filteredContacts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {t('contact.noContacts')}
                </TableCell>
              </TableRow>
            ) : (
              filteredContacts.map((contact) => (
                <TableRow key={contact._id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person fontSize="small" />
                      {`${contact.firstName} ${contact.lastName}`}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Email fontSize="small" />
                      {contact.email}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Subject fontSize="small" />
                      {contact.subject.length > 50
                        ? `${contact.subject.substring(0, 50)}...`
                        : contact.subject}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={contact.status}
                        onChange={(e) => handleStatusChange(contact._id, e.target.value)}
                      >
                        <MenuItem value="new">{t('contact.status.new')}</MenuItem>
                        <MenuItem value="read">{t('contact.status.read')}</MenuItem>
                        <MenuItem value="responded">{t('contact.status.responded')}</MenuItem>
                        <MenuItem value="archived">{t('contact.status.archived')}</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccessTime fontSize="small" />
                      {new Date(contact.createdAt).toLocaleDateString()}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={() => handleViewContact(contact)}
                      color="primary"
                    >
                      <Visibility />
                    </IconButton>
                    <IconButton
                      onClick={() => setDeleteDialog({ open: true, contact })}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, contact: null })}
      >
        <DialogTitle>{t('contact.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('contact.deleteConfirmMessage', {
              name: deleteDialog.contact ?
                `${deleteDialog.contact.firstName} ${deleteDialog.contact.lastName}` : ''
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, contact: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contact Detail Dialog */}
      {detailDialog.contact && (
        <ContactDetailDialog
          open={detailDialog.open}
          contact={detailDialog.contact}
          onClose={() => setDetailDialog({ open: false, contact: null })}
          onStatusChange={handleStatusChange}
        />
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contacts;